/**
 * <roro-shop-the-look> — drop-up de productos de `sections/roro-shop-the-look.liquid`.
 *
 * Las filas ya vienen pintadas en Liquid (una lista oculta por foto). Aquí
 * solo se decide cuál se enseña, se abre y se cierra el panel y se cuida el
 * foco. Nada de carrito ni de variantes: cada fila es un enlace a la ficha.
 *
 * Mismo comportamiento que el quick-add (`assets/roro-product-carousel.js`):
 * bloqueo de scroll con `overflow: hidden` en el body, clase
 * `roro-sheet-open` (esconde las barras pinchadas de la ficha), reflow forzado
 * en vez de rAF para arrancar la transición, Escape y trampa de foco. Sin
 * `pushState`: es el origen del salto arriba en iPhone que tuvo el carrito.
 *
 * Extra de este panel: se cierra arrastrándolo hacia abajo en táctil.
 *
 * En escritorio, además, pinta cada tarjeta de la columna de productos con el
 * color del fondo de su foto (ver `matchCardBackgrounds`).
 */
(function () {
  'use strict';

  if (customElements.get('roro-shop-the-look')) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var CLOSE_MS = 300;
  var DRAG_CLOSE_PX = 90;
  var SAMPLE_PX = 48;

  /**
   * Color medio del borde de una imagen, reducida a 48px de ancho. Devuelve
   * null si no se puede leer (lienzo bloqueado por venir de otro dominio), si
   * el fondo es transparente o si es oscuro: ahí mejor una tarjeta blanca que
   * un nombre ilegible encima.
   */
  function edgeColor(img) {
    try {
      var w = SAMPLE_PX;
      var h = Math.max(2, Math.round(SAMPLE_PX * img.naturalHeight / img.naturalWidth));
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var d = ctx.getImageData(0, 0, w, h).data;
      var r = 0, g = 0, b = 0, a = 0, n = 0;
      var add = function (x, y) {
        var i = (y * w + x) * 4;
        r += d[i]; g += d[i + 1]; b += d[i + 2]; a += d[i + 3]; n++;
      };
      for (var x = 0; x < w; x++) { add(x, 0); add(x, h - 1); }
      for (var y = 1; y < h - 1; y++) { add(0, y); add(w - 1, y); }
      r /= n; g /= n; b /= n; a /= n;
      if (a < 250 || (0.299 * r + 0.587 * g + 0.114 * b) < 215) return null;
      return 'rgb(' + Math.round(r) + ' ' + Math.round(g) + ' ' + Math.round(b) + ')';
    } catch (e) {
      return null;
    }
  }

  function paintCard(card, color) {
    card.style.setProperty('--rstl-card-bg', color);
    card.setAttribute('data-rstl-bg', '');
  }

  class RoroShopTheLook extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('[data-rstl-sheet]');
      if (!this.sheet) return;

      this.panel = this.sheet.querySelector('[data-rstl-panel]');
      this.titleEl = this.sheet.querySelector('[data-rstl-title]');
      this.scroller = this.sheet.querySelector('[data-rstl-scroll]');
      this.closeBtn = this.sheet.querySelector('.rstl-sheet__close');
      this.trigger = null;
      this.closeTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.addEventListener('click', this.onClick);
      this.panel.addEventListener('touchstart', this.onTouchStart, { passive: true });
      // No pasivo: hay que poder cancelar el scroll de la página mientras se arrastra.
      this.panel.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.panel.addEventListener('touchend', this.onTouchEnd);
      this.panel.addEventListener('touchcancel', this.onTouchEnd);

      this.matchCardBackgrounds();
    }

    /* ---------- Fondo de las tarjetas de escritorio ----------
       Cada foto de producto trae su propio fondo de estudio (de 242 a 254 de
       gris, según la sesión). Sobre una tarjeta de color fijo se veía un
       rectángulo. Se lee el color del borde de la foto y se pinta la tarjeta
       con él. Las imágenes van `lazy` y en `display: none` en móvil: ahí no se
       carga ninguna y esto no hace nada. */
    matchCardBackgrounds() {
      this.querySelectorAll('img.rstl__product-img').forEach(function (img) {
        var card = img.closest('.rstl__product-link');
        if (!card) return;
        var run = function () { this.readCardColor(img, card); }.bind(this);
        if (img.complete && img.naturalWidth) run();
        else img.addEventListener('load', run, { once: true });
      }, this);
    }

    readCardColor(img, card) {
      var color = edgeColor(img);
      if (color) {
        paintCard(card, color);
        return;
      }
      /* Imagen de otro dominio (vista previa, editor): el lienzo queda
         bloqueado. El CDN de Shopify responde con
         `access-control-allow-origin: *` (comprobado el 2026-09-24), así que
         se pide una copia de 48px con CORS y se lee esa. */
      var src = img.currentSrc || img.src;
      if (!src) return;
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        var probeColor = edgeColor(probe);
        if (probeColor) paintCard(card, probeColor);
      };
      probe.src = /[?&]width=\d+/.test(src)
        ? src.replace(/([?&])width=\d+/, '$1width=' + SAMPLE_PX)
        : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + SAMPLE_PX;
    }

    disconnectedCallback() {
      if (!this.sheet) return;
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      // El editor re-renderiza la sección: que no se quede la página bloqueada.
      if (!this.sheet.hidden) this.unlockScroll();
    }

    onClick(event) {
      var opener = event.target.closest('[data-rstl-open]');
      if (opener && this.contains(opener)) {
        event.preventDefault();
        this.open(opener);
        return;
      }

      if (event.target.closest('[data-rstl-close]')) {
        this.close();
        return;
      }

      // Tocar la foto del look (fuera de los puntos) abre el mismo drop-up que
      // «N товари» (2026-09-29): era un toque muerto en la foto más grande de la
      // sección. Solo si ese botón se ve, o sea hasta 989px; en escritorio la
      // columna de productos ya está al lado y la foto se queda como estaba.
      var frame = event.target.closest('.rstl__frame');
      var figure = frame && frame.closest('.rstl__figure');
      var count = figure && figure.querySelector('.rstl__count[data-rstl-open]');
      if (count && count.offsetParent !== null && this.contains(count)) {
        event.preventDefault();
        this.open(count);
        return;
      }

      // Un arrastre que acaba encima de una fila no debe abrir la ficha.
      if (this.suppressClick && event.target.closest('[data-rstl-item]')) {
        event.preventDefault();
      }
    }

    open(opener) {
      var lookId = opener.getAttribute('data-rstl-open');
      var list = this.sheet.querySelector('[data-rstl-list="' + lookId + '"]');
      if (!list) return;

      window.clearTimeout(this.closeTimer);

      this.sheet.querySelectorAll('[data-rstl-list]').forEach(function (el) {
        el.hidden = el !== list;
      });

      // La fila del punto tocado queda marcada; el «N товари» abre sin marca.
      var index = opener.classList.contains('rstl__dot')
        ? parseInt(opener.getAttribute('data-rstl-index'), 10)
        : -1;
      this.sheet.querySelectorAll('[data-rstl-item][aria-current]').forEach(function (item) {
        item.removeAttribute('aria-current');
      });
      var active = list.querySelectorAll('[data-rstl-item]')[index] || null;
      if (active) active.setAttribute('aria-current', 'true');

      if (this.titleEl) this.titleEl.textContent = list.getAttribute('data-rstl-heading') || '';
      if (this.scroller) this.scroller.scrollTop = 0;

      this.setExpanded(this.trigger, false);
      this.trigger = opener;
      this.setExpanded(opener, true);

      var wasOpen = !this.sheet.hidden && this.sheet.classList.contains('is-open');
      this.sheet.hidden = false;
      if (!wasOpen) {
        document.body.classList.add('roro-sheet-open');
        this.lockScroll();
        /* Reflow forzado, no rAF: con la pestaña sin pintar el rAF no llega
           y el panel se quedaba abierto pero invisible. Ver el quick-add. */
        void this.sheet.offsetHeight;
        this.sheet.classList.add('is-open');
        document.addEventListener('keydown', this.onKeydown);
      }

      var target = active || this.closeBtn;
      if (target) target.focus({ preventScroll: true });
    }

    close() {
      if (this.sheet.hidden) return;

      this.sheet.classList.remove('is-open', 'is-dragging');
      this.panel.style.transform = '';
      document.body.classList.remove('roro-sheet-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockScroll();

      var trigger = this.trigger;
      this.setExpanded(trigger, false);
      this.trigger = null;

      var sheet = this.sheet;
      this.closeTimer = window.setTimeout(function () {
        sheet.hidden = true;
        if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
      }, CLOSE_MS);
    }

    setExpanded(el, value) {
      if (!el) return;
      var lookId = el.getAttribute('data-rstl-open');
      // El punto y el «N товари» de la misma foto controlan el mismo panel.
      this.querySelectorAll('[data-rstl-open="' + lookId + '"]').forEach(function (node) {
        node.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      // En `<html>` y no en el body: con `overflow-x: clip` en el html (base.css),
      // `hidden` en el body lo convierte en contenedor de scroll y lo `sticky`
      // (la columna derecha de la ficha) salta a su sitio de arriba del todo.
      document.documentElement.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
    }

    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    /* ---------- Arrastrar hacia abajo para cerrar (solo táctil) ----------
       Solo arranca si la lista está arriba del todo: con la lista desplazada,
       bajar el dedo es hacer scroll dentro de ella, no cerrar. */
    onTouchStart(event) {
      if (event.touches.length !== 1) return;
      this.dragStartY = event.touches[0].clientY;
      this.dragDelta = 0;
      this.dragArmed = !this.scroller || this.scroller.scrollTop <= 0;
      this.dragging = false;
    }

    onTouchMove(event) {
      if (!this.dragArmed) return;
      var delta = event.touches[0].clientY - this.dragStartY;

      if (!this.dragging) {
        if (delta < 8) {
          // Hacia arriba es scroll normal de la lista: se suelta el gesto.
          if (delta < 0) this.dragArmed = false;
          return;
        }
        this.dragging = true;
        this.sheet.classList.add('is-dragging');
      }

      event.preventDefault();
      this.dragDelta = Math.max(0, delta);
      this.panel.style.transform = 'translateY(' + this.dragDelta + 'px)';
    }

    onTouchEnd() {
      if (!this.dragging) {
        this.dragArmed = false;
        return;
      }

      this.dragging = false;
      this.dragArmed = false;
      this.sheet.classList.remove('is-dragging');

      // Solo el click sintetico que sigue al gesto; el siguiente toque ya vale.
      var self = this;
      this.suppressClick = true;
      window.setTimeout(function () { self.suppressClick = false; }, 400);

      if (this.dragDelta > DRAG_CLOSE_PX) {
        this.close();
      } else {
        // Vuelve a su sitio con la transición normal del panel.
        this.panel.style.transform = '';
      }
    }
  }

  customElements.define('roro-shop-the-look', RoroShopTheLook);
})();
